import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { Users, Plane, AlertTriangle, Loader2, Trophy, ArrowUpRight } from 'lucide-react';
import { AvatarDisplay } from '../../utils/avatars';
import { Link } from 'react-router-dom';

export default function AdminHome() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsers: 0,
    totalEvents: 0,
    totalTravelPosts: 0,
    pendingReports: 0
  });
  const [topUsers, setTopUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // 1. Fetch Stats (Counts)
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        const [usersRes, newUsersRes, eventsRes, travelRes, reportsRes] = await Promise.all([
          supabase.from('profiles').select('id', { count: 'exact', head: true }),
          supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', oneWeekAgo.toISOString()),
          supabase.from('events').select('id', { count: 'exact', head: true }),
          supabase.from('travel_posts').select('id', { count: 'exact', head: true }),
          supabase.from('reports').select('id', { count: 'exact', head: true }).eq('status', 'pending')
        ]);

        setStats({
          totalUsers: usersRes.count || 0,
          newUsers: newUsersRes.count || 0,
          totalEvents: eventsRes.count || 0,
          totalTravelPosts: travelRes.count || 0,
          pendingReports: reportsRes.count || 0
        });

        // 2. Fetch Top Active Users
        // 由於 Supabase 無法直接用語法簡單算出 JOIN GROUP BY 的總和排序，
        // 這裡我們先撈取最近一個月的 events，並在前端計算活躍度。
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        const { data: recentEvents } = await supabase
          .from('events')
          .select('organizer_id')
          .gte('created_at', oneMonthAgo.toISOString());

        const { data: recentTravels } = await supabase
          .from('travel_posts')
          .select('organizer_id')
          .gte('created_at', oneMonthAgo.toISOString());

        // 計算每個用戶的活動數量
        const userActivityMap = {};
        const countActivity = (dataArray, weight = 1) => {
          if (!dataArray) return;
          dataArray.forEach(item => {
            const uid = item.organizer_id;
            if (!userActivityMap[uid]) userActivityMap[uid] = 0;
            userActivityMap[uid] += weight;
          });
        };

        countActivity(recentEvents, 1);
        countActivity(recentTravels, 1);

        // 排序並取 Top 20
        const sortedUserIds = Object.entries(userActivityMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 20)
          .map(entry => entry[0]);

        if (sortedUserIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, name, avatar, created_at')
            .in('id', sortedUserIds);

          // 將 profiles 依照分數排序
          if (profilesData) {
            const finalTopUsers = profilesData
              .map(p => ({ ...p, score: userActivityMap[p.id] }))
              .sort((a, b) => b.score - a.score);
            setTopUsers(finalTopUsers);
          }
        }

      } catch (err) {
        console.error('Fetch dashboard data error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const StatCard = ({ title, value, icon: Icon, color, bg, subtitle, link }) => (
    <div style={{ background: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '20px' }}>
      <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: color }}>
        <Icon size={28} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ margin: '0 0 4px', fontSize: '14px', color: '#64748b', fontWeight: '600' }}>{title}</p>
        <h3 style={{ margin: 0, fontSize: '28px', fontWeight: '800', color: '#1e293b' }}>{value}</h3>
        {subtitle && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94a3b8' }}>{subtitle}</p>}
      </div>
      {link && (
        <Link to={link} style={{ padding: '8px', background: '#f1f5f9', borderRadius: '50%', color: '#64748b', display: 'flex' }}>
          <ArrowUpRight size={18} />
        </Link>
      )}
    </div>
  );

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><Loader2 className="animate-spin" size={40} color="#4e8bca" /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        <StatCard
          title="總用戶數"
          value={stats.totalUsers}
          subtitle={`本週新註冊: +${stats.newUsers}人`}
          icon={Users} color="#6a9fd3" bg="#f6f9fc"
          link="/admin/users"
        />
        <StatCard
          title="待處理檢舉"
          value={stats.pendingReports}
          subtitle={stats.pendingReports > 0 ? '需盡速處理！' : '目前社群很和平'}
          icon={AlertTriangle} color={stats.pendingReports > 0 ? "#ef4444" : "#10b981"} bg={stats.pendingReports > 0 ? "#fef2f2" : "#d1fae5"}
          link="/admin/reports"
        />
        <StatCard
          title="總揪團活動"
          value={stats.totalEvents}
          icon={CalendarCheck} color="#0ea5e9" bg="#e0f2fe"
        />
        <StatCard
          title="總旅行貼文"
          value={stats.totalTravelPosts}
          icon={Plane} color="#f59e0b" bg="#f6f9fc"
        />
      </div>

      {/* Top Users */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <Trophy color="#6a9fd3" size={24} />
          <h2 style={{ fontSize: '1.25rem', color: '#1e293b', margin: 0 }}>活躍用戶排行榜 (Top 20)</h2>
          <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: 'auto' }}>*基於近一個月的發布活動數</span>
        </div>

        {topUsers.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {topUsers.map((user, index) => (
              <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', border: '1px solid #f1f5f9', borderRadius: '12px', background: index < 3 ? '#f6f9fc' : 'white' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: index === 0 ? '#6a9fd3' : index === 1 ? '#94a3b8' : index === 2 ? '#b45309' : '#cbd5e1', width: '24px', textAlign: 'center' }}>
                  #{index + 1}
                </div>
                <AvatarDisplay avatarId={user.avatar || 'avatar1'} size={48} />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px', fontSize: '15px', color: '#1e293b' }}>{user.name}</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>活躍指數: <strong>{user.score}</strong> 篇</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
            近一個月內尚無活躍紀錄
          </div>
        )}
      </div>

    </div>
  );
}

// Just add this helper icon if lucide-react hasn't exported CalendarCheck
const CalendarCheck = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
    <path d="m9 16 2 2 4-4"></path>
  </svg>
);
